import { readDetails } from "./companyDetails";
import { pastedKind } from "./pastedBusiness";
import { findSimilarRecord, SimilarRecord } from "./similarRecord";
import { isPermissionGranted } from "./navPermissions";
import { PagesPermissions } from "../types/Authorization";
import type { AttachAction } from "./askAttachmentPlan";

/**
 * "And I also want to add Laila to our contacts", "save her as a lead",
 * "update the client from it" — after a message was pasted into Ask
 * (user, 2026-10-15: "if asked to add/create/update, it's supposed to fill
 * the form and I save the normal way"). Read by CODE on this computer: the
 * form opens filled in from the pasted message, so the names, phones and
 * emails in its signature never reach the AI (D18), and no credit is used.
 */

export type AskedRecord = "contact" | "lead" | "client" | "provider" | "company";

export interface RecordAsk {
    kind: AskedRecord;
    /** "update", "change", "edit": the record already in CRX gets the new details. */
    update: boolean;
}

const VERB = /\b(?:add|create|save|make|register|put|set\s*up|new|get|update|change|edit|enter|record|file|keep)\b|أضف|اضف|أضيف|اضيف|ضيف|أنشئ|انشئ|سجل|سجّل|احفظ|حدث|حدّث|عدل|عدّل/i;
const UPDATE = /\b(?:update|change|edit|correct)\b|حدث|حدّث|عدل|عدّل/i;
/** Other things Ask prepares itself (a task for her, a request for them): not this. */
const OTHER_RECORDS = /\b(?:tasks?|reminders?|requests?|missions?|movements?|legs?|activit(?:y|ies)|calls?|meetings?|ratings?|quotes?|leave|invoices?|drivers?|vehicles?|incidents?|notes?)\b|مهمة|مهمه|طلب|تذكير|ملاحظة/i;
const KIND_WORDS: Array<[RegExp, AskedRecord]> = [
    [/\bleads?\b|عميل\s+محتمل/i, "lead"],
    [/\bcontacts?\b|جهات?\s+(?:ال)?اتصال/i, "contact"],
    [/\bclients?\b|\bcustomers?\b|عميل|زبون/i, "client"],
    [/\bproviders?\b|\bsuppliers?\b|\bvendors?\b|مزود|مورد/i, "provider"],
    [/\bcompan(?:y|ies)\b|شركة/i, "company"],
];
/** Words that point at the message's sender or its details. */
const REFERENT = /\b(?:her|him|them|she|he|they|this person|that person|the sender)\b|\b(?:details|info|information)\s+(?:from|in)\s+(?:the|this|that|her|his)\s+(?:message|email|e-mail|chat|signature|whatsapp)\b|\bfrom\s+(?:it|the\s+(?:message|email|chat|signature))\b|تفاصيلها|تفاصيله|بياناتها|بياناته|منها|منه/i;
/** Capitalised words that aren't names (sentence words, the kinds, our own words). */
const NOT_A_NAME = /^(?:I|And|Also|Please|Can|Could|Yes|Ok|Okay|Add|Create|Save|Make|Update|New|Our|The|To|As|A|An|Contact|Contacts|Lead|Client|Provider|Company|Her|Him|Them|CRX|Ask)$/;

/** A name in the plain form its spellings share: Laila ≈ Layla, Mohamed ≈ Mohammed, Yousef ≈ Youssef. */
const plainName = (word: string): string => word.toLowerCase().replace(/[^a-z\u0621-\u064a]/g, "")
    .replace(/y/g, "i").replace(/ee/g, "i").replace(/ou|oo/g, "u").replace(/(.)\1+/g, "$1");

/** Whether two written names are the same name in different spellings (equal, or one letter apart from 5 letters). */
const sameName = (a: string, b: string): boolean => {
    const x = plainName(a);
    const y = plainName(b);
    if (!x || !y) return false;
    if (x === y) return true;
    if (Math.min(x.length, y.length) < 5 || Math.abs(x.length - y.length) > 1) return false;
    let edits = 0;
    for (let i = 0, j = 0; i < x.length || j < y.length;) {
        if (x[i] === y[j]) { i++; j++; continue; }
        if (++edits > 1) return false;
        if (x.length > y.length) i++;
        else if (y.length > x.length) j++;
        else { i++; j++; }
    }
    return true;
};

/**
 * Whether the person asks for a contact, lead, client or provider from the
 * message pasted earlier in the chat. A name that isn't the message's sender
 * or company ("add Yousef to contacts") isn't this — only the sender is read.
 * @param words - What the person typed now.
 * @param source - The message pasted earlier in this chat.
 */
export const recordAskFrom = (words: string, source: string): RecordAsk | null => {
    const text = String(words ?? "").trim();
    if (!text || !VERB.test(text) || OTHER_RECORDS.test(text)) return null;
    const details = readDetails(source);
    if (!details.person && !details.company) return null;
    const lower = text.toLowerCase();
    const known = [details.person, ...(details.person ?? "").split(/\s+/), details.personArabic, ...(details.personArabic ?? "").split(/\s+/), details.company, details.companyArabic]
        .filter((name): name is string => !!name && name.trim().length >= 3)
        .map((name) => name.toLowerCase());
    const knownWords = known.flatMap((name) => name.split(/\s+/)).filter((word) => word.length >= 3);
    const typed = text.match(/[\p{L}][\p{L}'-]{2,}/gu) ?? [];
    // the sender or the company, as written or spelt another way ("Laila" for Layla)
    const namesIt = known.some((name) => lower.includes(name)) || typed.some((word) => knownWords.some((name) => sameName(word, name)));
    // someone else named ("add Yousef …"): not the sender, so not this
    const others = (text.match(/\b[\p{Lu}][\p{L}'-]{2,}\b/gu) ?? []).filter((word) => !NOT_A_NAME.test(word) && !knownWords.some((name) => name.includes(word.toLowerCase()) || sameName(word, name)));
    if (others.length && !namesIt) return null;
    const said = KIND_WORDS.find(([pattern]) => pattern.test(text))?.[1] ?? null;
    if (!said && !REFERENT.test(text) && !namesIt) return null;
    // "add her" / "add Laila": the person; "add them as a client": the kind said
    const kind: AskedRecord | null = said ?? (details.person ? "contact" : details.company ? "client" : null);
    if (!kind) return null;
    // a kind word with nothing pointing at the message ("create a new client") is a typed create, read elsewhere —
    // unless it's "add / save a contact" right after the paste
    const linked = namesIt || REFERENT.test(text) || /\b(?:to|in|into)\s+(?:our|the|my)\s+(?:contacts|leads|clients|providers)\b/i.test(text);
    if (!linked && !/\b(?:add|save)\b|أضف|اضف|احفظ/i.test(text)) return null;
    return { kind, update: UPDATE.test(text) };
};

/**
 * The latest message pasted into this chat (an email, a WhatsApp chat, a
 * signature) — the person's own text, as shown on screen.
 * @param messages - The chat's messages, oldest first.
 */
export const pastedSource = (messages: Array<{ role: string; text: string }>): string | null => {
    for (let i = messages.length - 1; i >= 0; i--) {
        const message = messages[i];
        if (message.role !== "user") continue;
        const kind = pastedKind(message.text);
        if (kind === "message" || kind === "business") return message.text;
    }
    return null;
};

const NOUN: Record<AskedRecord, string> = { contact: "contact", lead: "lead", client: "client", provider: "provider", company: "company" };
const CREATE_RIGHT: Record<AskedRecord, string> = {
    contact: PagesPermissions.CONTACTS_CREATE, lead: PagesPermissions.LEADS_CREATE, client: PagesPermissions.CLIENTS_CREATE,
    provider: PagesPermissions.PROVIDERS_CREATE, company: PagesPermissions.COMPANIES_CREATE,
};
const EDIT_RIGHT: Record<AskedRecord, string> = {
    contact: PagesPermissions.CONTACTS_EDIT, lead: PagesPermissions.LEADS_EDIT, client: PagesPermissions.CLIENTS_EDIT,
    provider: PagesPermissions.PROVIDERS_EDIT, company: PagesPermissions.COMPANIES_EDIT,
};

/**
 * Ask's answer, read here: what the message gives for the record, and the
 * button that opens the form filled in (a new one, or the one already in CRX
 * with each change current → new). Null without the right to do it.
 * @param ask - What was asked for.
 * @param source - The pasted message.
 * @param pages - The person's page permissions.
 * @param find - Looks a name up in CRX as the person (injected for tests).
 */
export const recordAskReply = async (
    ask: RecordAsk,
    source: string,
    pages: any[],
    find: typeof findSimilarRecord = findSimilarRecord,
): Promise<{ message: string; actions: AttachAction[] } | null> => {
    const details = readDetails(source);
    const can = (right: string) => isPermissionGranted(pages, right);
    const noun = NOUN[ask.kind];
    const isPerson = ask.kind === "contact" || ask.kind === "lead";
    const name = ask.kind === "contact" ? details.person : ask.kind === "lead" ? details.company ?? details.person : details.company;
    if (!name) return null;
    // already in CRX? (searched as the person; a lead is always new from a message)
    const existing: SimilarRecord | null = ask.kind === "lead" ? null : await find(name, [ask.kind]).catch(() => null);
    const org = details.company && ask.kind === "contact" ? await find(details.company, ["client", "provider", "company"]).catch(() => null) : null;
    const spelling = details.personArabic && isPerson ? " (from Arabic — check spelling)" : details.companyArabic && !isPerson ? " (from Arabic — check spelling)" : "";
    const facts = [
        `✓ Name: ${name}${spelling}`,
        ask.kind === "contact" && details.title ? `✓ Job title: ${details.title}` : null,
        ask.kind === "contact" && details.company ? `✓ Organization: ${org ? `${org.name}${org.exact ? " (in CRX)" : " (the closest in CRX — check it)"}` : `${details.company} (not in CRX yet — pick it on the form)`}` : null,
        ask.kind === "lead" && details.person ? `✓ Contact person: ${details.person}` : null,
        details.phones.length ? `✓ Phone${details.phones.length > 1 ? "s" : ""}: ${details.phones.map((phone) => phone.number).join(", ")}` : null,
        details.emails.length ? `✓ Email: ${details.emails.join(", ")}` : null,
        details.website ? `✓ Website: ${details.website}` : null,
    ].filter(Boolean).join("\n");

    if (existing && (existing.exact || ask.update)) {
        if (!can(EDIT_RIGHT[ask.kind])) return null;
        return {
            message: `${existing.name} is already in CRX${existing.exact ? "" : " (the closest match — check it's the right one)"}. Open it with the details from the message — each change shows as current → new to tick; nothing is saved until you save:\n${facts}`,
            actions: [{
                label: `Fill with AI: update ${existing.name}`, icon: "stars",
                title: `Open ${existing.name} with Fill with AI reading the message on your computer — tick what changes, then save.`,
                go: { type: "record_update", kind: existing.kind, id: existing.id, text: source },
            }],
        };
    }
    if (!can(CREATE_RIGHT[ask.kind])) return null;
    const go: AttachAction["go"] = ask.kind === "contact"
        ? { type: "contact", text: source, organizationName: details.company ?? undefined }
        : ask.kind === "lead"
            ? { type: "lead", text: source }
            : { type: "record_new", kind: ask.kind, text: source, name: details.company ?? undefined };
    return {
        message: `A new ${noun} from the message — read on your computer, nothing sent to the AI:\n${facts}\nOpen it to check, then save as usual.`,
        actions: [{
            label: `Fill with AI: new ${noun}`, icon: "stars",
            title: `Open a new ${noun} filled in from the message (read on your computer) — you check each value and save.`,
            go,
        }],
    };
};
